import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { 
  FiTarget, FiCheck, FiX, FiAlertCircle, FiAlertTriangle, 
  FiArrowUp, FiArrowDown, FiMinus, FiInfo, FiBarChart2,
  FiUpload, FiDownload, FiPlus, FiEdit, FiTrash2
} = FiIcons;

const RaschAnalysis = ({ questions }) => {
  const [responseData, setResponseData] = useState({});
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [fileData, setFileData] = useState(null);
  const [uploadStatus, setUploadStatus] = useState({ loading: false, error: null });
  const [analysisResults, setAnalysisResults] = useState(null);
  const [manualEntryMode, setManualEntryMode] = useState(false);
  
  // Generate placeholder response data structure for manual entry
  const generateEmptyResponseData = () => {
    const emptyData = {};
    questions.forEach(question => {
      emptyData[question.id] = {
        questionId: question.id,
        responses: Array(5).fill().map(() => ({
          studentId: '',
          response: '',
          isCorrect: false,
          score: 0
        }))
      };
    });
    return emptyData;
  };
  
  // Initialize manual entry data
  const initializeManualEntry = () => {
    setManualEntryMode(true);
    setResponseData(generateEmptyResponseData());
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploadStatus({ loading: true, error: null });
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target.result);
        setFileData(data);
        processResponseData(data);
      } catch (error) {
        setUploadStatus({ loading: false, error: 'Invalid file format. Please upload a valid JSON file.' });
      }
    };
    
    reader.onerror = () => {
      setUploadStatus({ loading: false, error: 'Error reading file.' });
    };
    
    reader.readAsText(file);
  };

  // Process the response data and run Rasch analysis
  const processResponseData = (data) => {
    try {
      // Validate data structure
      if (!data || !data.responses || !Array.isArray(data.responses)) {
        throw new Error('Invalid data structure');
      }
      
      // Map responses to questions
      const mappedResponses = {};
      data.responses.forEach(response => {
        if (!mappedResponses[response.questionId]) {
          mappedResponses[response.questionId] = {
            questionId: response.questionId,
            responses: []
          };
        }
        mappedResponses[response.questionId].responses.push(response);
      });
      
      setResponseData(mappedResponses);
      runRaschAnalysis(mappedResponses);
      
      setUploadStatus({ loading: false, error: null });
      setShowUploadModal(false);
    } catch (error) {
      setUploadStatus({ 
        loading: false, 
        error: `Error processing data: ${error.message}`
      });
    }
  };

  // Run Rasch analysis on the response data
  const runRaschAnalysis = (data) => {
    // In a real implementation, this would use a proper Rasch model algorithm
    // For demonstration, we'll calculate some simple metrics
    
    const results = questions.map(question => {
      const questionResponses = data[question.id]?.responses || [];
      const totalResponses = questionResponses.length;
      
      // Skip questions with no responses
      if (totalResponses === 0) {
        return {
          ...question,
          difficulty: null,
          infit: null,
          outfit: null,
          discrimination: null,
          pointBiserial: null,
          fitStatus: 'unknown',
          discriminationRating: 'unknown',
          recommendation: 'No response data available',
          totalResponses: 0,
          correctResponses: 0
        };
      }
      
      // Calculate percentage of correct answers (p-value)
      const correctResponses = questionResponses.filter(r => r.isCorrect).length;
      const pValue = correctResponses / totalResponses;
      
      // Convert p-value to logit difficulty (simplified transformation)
      const difficulty = totalResponses > 0 
        ? parseFloat((Math.log((1 - pValue) / pValue)).toFixed(2)) 
        : 0;
      
      // Simulate infit and outfit statistics
      // In a real implementation, these would be calculated using the Rasch model
      const standardizedResiduals = questionResponses.map(r => {
        const expected = 1 / (1 + Math.exp(-difficulty));
        return (r.isCorrect ? 1 : 0) - expected;
      });
      
      const squaredResiduals = standardizedResiduals.map(r => r * r);
      const infit = parseFloat((squaredResiduals.reduce((a, b) => a + b, 0) / totalResponses).toFixed(2));
      const outfit = parseFloat((Math.sqrt(squaredResiduals.reduce((a, b) => a + b, 0) / totalResponses)).toFixed(2));
      
      // Calculate point-biserial correlation (simplified)
      const discrimination = parseFloat((1 - pValue * (1 - pValue)).toFixed(2));
      const pointBiserial = parseFloat((Math.sqrt(pValue * (1 - pValue))).toFixed(2));
      
      // Determine fit status
      const fitStatus = 
        (infit >= 0.7 && infit <= 1.3 && outfit >= 0.7 && outfit <= 1.3)
          ? 'good'
          : (infit < 0.5 || infit > 1.5 || outfit < 0.5 || outfit > 1.5)
            ? 'poor'
            : 'acceptable';
            
      // Discrimination rating
      const discriminationRating = 
        discrimination >= 0.7 ? 'excellent' :
        discrimination >= 0.5 ? 'good' :
        discrimination >= 0.3 ? 'acceptable' :
        'poor';
      
      return {
        ...question,
        difficulty,
        infit,
        outfit,
        discrimination,
        pointBiserial,
        fitStatus,
        discriminationRating,
        recommendation: getRecommendation(fitStatus, discriminationRating, difficulty),
        totalResponses,
        correctResponses
      };
    });
    
    // Calculate summary statistics
    const validResults = results.filter(r => r.totalResponses > 0);
    const summaryStats = {
      avgDifficulty: parseFloat(
        (validResults.reduce((sum, q) => sum + q.difficulty, 0) / validResults.length).toFixed(2)
      ),
      avgInfit: parseFloat(
        (validResults.reduce((sum, q) => sum + q.infit, 0) / validResults.length).toFixed(2)
      ),
      avgOutfit: parseFloat(
        (validResults.reduce((sum, q) => sum + q.outfit, 0) / validResults.length).toFixed(2)
      ),
      avgDiscrimination: parseFloat(
        (validResults.reduce((sum, q) => sum + q.discrimination, 0) / validResults.length).toFixed(2)
      ),
      goodFitCount: validResults.filter(q => q.fitStatus === 'good').length,
      acceptableFitCount: validResults.filter(q => q.fitStatus === 'acceptable').length,
      poorFitCount: validResults.filter(q => q.fitStatus === 'poor').length,
      totalResponses: Object.values(data).reduce((sum, q) => sum + q.responses.length, 0),
      totalQuestions: validResults.length
    };
    
    setAnalysisResults({
      items: results,
      summary: summaryStats,
      sortedItems: [...validResults].sort((a, b) => a.difficulty - b.difficulty)
    });
  };

  // Handle manual entry form changes
  const handleResponseChange = (questionId, responseIndex, field, value) => {
    setResponseData(prev => {
      const updatedData = { ...prev };
      const question = { ...updatedData[questionId] };
      const responses = [...question.responses];
      responses[responseIndex] = { 
        ...responses[responseIndex], 
        [field]: field === 'isCorrect' ? value === 'true' : value 
      };
      question.responses = responses;
      updatedData[questionId] = question;
      return updatedData;
    });
  };

  // Add a new response row to a question
  const addResponseRow = (questionId) => {
    setResponseData(prev => {
      const updatedData = { ...prev };
      const question = { ...updatedData[questionId] };
      const responses = [...question.responses, {
        studentId: `Student${question.responses.length + 1}`,
        response: '',
        isCorrect: false,
        score: 0
      }];
      question.responses = responses;
      updatedData[questionId] = question;
      return updatedData;
    });
  };

  // Remove a response row from a question
  const removeResponseRow = (questionId, responseIndex) => {
    setResponseData(prev => {
      const updatedData = { ...prev };
      const question = { ...updatedData[questionId] };
      const responses = question.responses.filter((_, idx) => idx !== responseIndex);
      question.responses = responses;
      updatedData[questionId] = question;
      return updatedData;
    });
  };

  // Run analysis on manual entry data
  const runManualAnalysis = () => {
    runRaschAnalysis(responseData);
  };

  // Export response data as JSON
  const exportResponseData = () => {
    const dataStr = JSON.stringify({
      responses: Object.values(responseData).flatMap(q => q.responses.map(r => ({
        questionId: q.questionId,
        ...r
      })))
    }, null, 2);
    
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'response_data.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  
  // Get recommendation based on fit and discrimination
  function getRecommendation(fitStatus, discriminationRating, difficulty) {
    if (fitStatus === 'poor') {
      return "Review or replace this item";
    }
    if (fitStatus === 'acceptable' && discriminationRating === 'poor') {
      return "Consider revision for better discrimination";
    }
    if (discriminationRating === 'excellent' && fitStatus === 'good') {
      return "Excellent item, retain";
    }
    if (Math.abs(difficulty) > 1.5) {
      return `${difficulty > 0 ? 'Difficult' : 'Easy'} item, use strategically`;
    }
    return "Good item, retain";
  }
  
  // Get status color
  function getStatusColor(status) {
    switch(status) {
      case 'good':
      case 'excellent': return 'text-green-600';
      case 'acceptable': return 'text-yellow-600';
      case 'poor': return 'text-red-600';
      default: return 'text-gray-600';
    }
  }
  
  // Get status icon
  function getStatusIcon(status) {
    switch(status) {
      case 'good':
      case 'excellent': return FiCheck;
      case 'acceptable': return FiAlertCircle;
      case 'poor': return FiX;
      default: return FiInfo;
    }
  }
  
  // Sample response data structure
  const sampleData = {
    "responses": [
      {
        "questionId": "q1",
        "studentId": "s1",
        "response": "A",
        "isCorrect": true,
        "score": 1
      },
      {
        "questionId": "q1",
        "studentId": "s2",
        "response": "B",
        "isCorrect": false,
        "score": 0
      }
    ]
  };
  
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-800 flex items-center">
            <div className="p-2 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg text-white mr-3">
              <SafeIcon icon={FiTarget} />
            </div>
            Rasch-friendly Analysis
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Evaluates items based on Rasch model fit statistics and discrimination parameters
          </p>
        </div>
        <div className="bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full text-xs font-medium">
          Advanced Analysis
        </div>
      </div>

      {/* Response Data Input Section */}
      {!analysisResults && (
        <div className="mb-6 p-5 bg-indigo-50 rounded-lg border border-indigo-100">
          <h4 className="text-md font-semibold text-indigo-800 mb-3">
            Response Data Required
          </h4>
          <p className="text-sm text-indigo-700 mb-4">
            Rasch analysis requires student response data to calculate item parameters. Please upload response data or enter it manually.
          </p>
          
          <div className="flex flex-wrap gap-4 mt-4">
            <motion.button
              onClick={() => setShowUploadModal(true)}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg flex items-center space-x-2 hover:bg-indigo-700"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <SafeIcon icon={FiUpload} />
              <span>Upload Response Data</span>
            </motion.button>
            
            <motion.button
              onClick={initializeManualEntry}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg flex items-center space-x-2 hover:bg-purple-700"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <SafeIcon icon={FiEdit} />
              <span>Enter Response Data Manually</span>
            </motion.button>
            
            <motion.button
              onClick={() => window.open('data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(sampleData, null, 2)))}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg flex items-center space-x-2 hover:bg-gray-700"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <SafeIcon icon={FiDownload} />
              <span>Download Sample Format</span>
            </motion.button>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }} 
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg p-6 max-w-lg w-full"
          >
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Upload Response Data</h3>
            
            <p className="text-sm text-gray-600 mb-4">
              Upload a JSON file containing student responses to calculate Rasch parameters. The file should follow the structure shown in the example below.
            </p>
            
            <div className="bg-gray-50 p-3 rounded-lg mb-4 overflow-x-auto">
              <pre className="text-xs text-gray-700">
                {JSON.stringify(sampleData, null, 2)}
              </pre>
            </div>
            
            <input 
              type="file" 
              accept=".json" 
              onChange={handleFileUpload}
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-lg file:border-0
                file:text-sm file:font-semibold
                file:bg-indigo-50 file:text-indigo-700
                hover:file:bg-indigo-100
                mb-4"
            />
            
            {uploadStatus.error && (
              <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-4">
                {uploadStatus.error}
              </div>
            )}
            
            <div className="flex justify-end space-x-3">
              <button 
                onClick={() => setShowUploadModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                disabled={uploadStatus.loading}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg flex items-center space-x-2 hover:bg-indigo-700 disabled:bg-gray-400"
              >
                {uploadStatus.loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Processing...</span>
                  </>
                ) : (
                  <span>Upload</span>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Manual Entry Form */}
      {manualEntryMode && !analysisResults && (
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-md font-semibold text-gray-800">
              Manual Response Data Entry
            </h4>
            <div className="flex space-x-3">
              <motion.button
                onClick={runManualAnalysis}
                className="px-4 py-2 bg-green-600 text-white rounded-lg flex items-center space-x-2 hover:bg-green-700"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <SafeIcon icon={FiBarChart2} />
                <span>Run Analysis</span>
              </motion.button>
              
              <motion.button
                onClick={exportResponseData}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center space-x-2 hover:bg-blue-700"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <SafeIcon icon={FiDownload} />
                <span>Export Data</span>
              </motion.button>
            </div>
          </div>
          
          <div className="space-y-6 max-h-96 overflow-y-auto p-2">
            {questions.map((question, qIndex) => (
              <div key={question.id} className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium text-gray-800 mb-2">
                  Question {qIndex + 1}: {question.text?.substring(0, 100)}...
                </h5>
                
                <div className="bg-gray-50 p-3 rounded-lg mb-3">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2">Student ID</th>
                        <th className="text-left py-2">Response</th>
                        <th className="text-left py-2">Correct?</th>
                        <th className="text-left py-2">Score</th>
                        <th className="text-left py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {responseData[question.id]?.responses.map((response, rIndex) => (
                        <tr key={rIndex} className="border-b border-gray-100">
                          <td className="py-2">
                            <input
                              type="text"
                              value={response.studentId}
                              onChange={(e) => handleResponseChange(question.id, rIndex, 'studentId', e.target.value)}
                              className="w-full p-1 border border-gray-300 rounded"
                              placeholder="Student ID"
                            />
                          </td>
                          <td className="py-2">
                            <input
                              type="text"
                              value={response.response}
                              onChange={(e) => handleResponseChange(question.id, rIndex, 'response', e.target.value)}
                              className="w-full p-1 border border-gray-300 rounded"
                              placeholder="Response"
                            />
                          </td>
                          <td className="py-2">
                            <select
                              value={response.isCorrect.toString()}
                              onChange={(e) => handleResponseChange(question.id, rIndex, 'isCorrect', e.target.value)}
                              className="w-full p-1 border border-gray-300 rounded"
                            >
                              <option value="true">Yes</option>
                              <option value="false">No</option>
                            </select>
                          </td>
                          <td className="py-2">
                            <input
                              type="number"
                              min="0"
                              max="100"
                              value={response.score}
                              onChange={(e) => handleResponseChange(question.id, rIndex, 'score', parseInt(e.target.value))}
                              className="w-full p-1 border border-gray-300 rounded"
                              placeholder="Score"
                            />
                          </td>
                          <td className="py-2">
                            <button
                              onClick={() => removeResponseRow(question.id, rIndex)}
                              className="p-1 text-red-600 hover:text-red-800"
                              title="Remove response"
                            >
                              <SafeIcon icon={FiTrash2} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  <button
                    onClick={() => addResponseRow(question.id)}
                    className="mt-2 px-3 py-1 bg-gray-200 text-gray-700 rounded flex items-center space-x-1 text-sm hover:bg-gray-300"
                  >
                    <SafeIcon icon={FiPlus} className="text-xs" />
                    <span>Add Response</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Analysis Results */}
      {analysisResults && (
        <>
          {/* Reset Button */}
          <div className="mb-4 flex justify-end">
            <motion.button
              onClick={() => {
                setAnalysisResults(null);
                setManualEntryMode(false);
                setResponseData({});
              }}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg flex items-center space-x-2 hover:bg-gray-700"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <SafeIcon icon={FiX} />
              <span>Reset Analysis</span>
            </motion.button>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-100">
              <div className="text-sm text-purple-800 font-medium mb-1">Average Difficulty</div>
              <div className="text-2xl font-bold text-indigo-900">{analysisResults.summary.avgDifficulty} logits</div>
              <div className="text-xs text-purple-700 mt-1">
                {analysisResults.summary.avgDifficulty > 0.5 ? 'Challenging' : analysisResults.summary.avgDifficulty < -0.5 ? 'Easy' : 'Balanced'} question set
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-100">
              <div className="text-sm text-blue-800 font-medium mb-1">Average Infit/Outfit</div>
              <div className="text-2xl font-bold text-blue-900">{analysisResults.summary.avgInfit}/{analysisResults.summary.avgOutfit}</div>
              <div className="text-xs text-blue-700 mt-1">
                {(analysisResults.summary.avgInfit >= 0.7 && analysisResults.summary.avgInfit <= 1.3 && analysisResults.summary.avgOutfit >= 0.7 && analysisResults.summary.avgOutfit <= 1.3) 
                  ? 'Good overall fit' 
                  : 'Some fit issues'}
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-4 rounded-lg border border-green-100">
              <div className="text-sm text-green-800 font-medium mb-1">Average Discrimination</div>
              <div className="text-2xl font-bold text-green-900">{analysisResults.summary.avgDiscrimination}</div>
              <div className="text-xs text-green-700 mt-1">
                {analysisResults.summary.avgDiscrimination >= 0.7 ? 'Excellent' : 
                analysisResults.summary.avgDiscrimination >= 0.5 ? 'Good' : 
                analysisResults.summary.avgDiscrimination >= 0.3 ? 'Acceptable' : 'Poor'} discrimination
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-amber-50 to-yellow-50 p-4 rounded-lg border border-amber-100">
              <div className="text-sm text-amber-800 font-medium mb-1">Item Fit Quality</div>
              <div className="flex items-center">
                <div className="h-4 bg-green-500 rounded-l" style={{width: `${(analysisResults.summary.goodFitCount/analysisResults.summary.totalQuestions)*100}%`}}></div>
                <div className="h-4 bg-yellow-500" style={{width: `${(analysisResults.summary.acceptableFitCount/analysisResults.summary.totalQuestions)*100}%`}}></div>
                <div className="h-4 bg-red-500 rounded-r" style={{width: `${(analysisResults.summary.poorFitCount/analysisResults.summary.totalQuestions)*100}%`}}></div>
              </div>
              <div className="flex justify-between text-xs mt-1">
                <span className="text-green-700">{analysisResults.summary.goodFitCount} good</span>
                <span className="text-yellow-700">{analysisResults.summary.acceptableFitCount} acceptable</span>
                <span className="text-red-700">{analysisResults.summary.poorFitCount} poor</span>
              </div>
            </div>
          </div>

          {/* Item Map Visualization */}
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Item Difficulty Map</h4>
            <div className="relative h-16 bg-gray-100 rounded-lg mb-1">
              <div className="absolute inset-x-0 top-1/2 h-0.5 bg-gray-400"></div>
              
              {/* Difficulty scale markers */}
              {[-2, -1, 0, 1, 2].map(value => (
                <div 
                  key={value} 
                  className="absolute top-0 bottom-0 flex flex-col items-center justify-between"
                  style={{ left: `${((value + 2) / 4) * 100}%` }}
                >
                  <div className="w-0.5 h-3 bg-gray-400"></div>
                  <span className="text-xs text-gray-600">{value}</span>
                  <div className="w-0.5 h-3 bg-gray-400"></div>
                </div>
              ))}
              
              {/* Item dots */}
              {analysisResults.sortedItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: index * 0.05, type: "spring" }}
                  className={`absolute w-4 h-4 rounded-full -mt-2 border-2 border-white cursor-pointer transform -translate-x-2`}
                  style={{ 
                    left: `${((item.difficulty + 2) / 4) * 100}%`,
                    top: '50%',
                    backgroundColor: 
                      item.fitStatus === 'good' ? '#10b981' : 
                      item.fitStatus === 'acceptable' ? '#f59e0b' : '#ef4444',
                  }}
                  title={`${item.text?.substring(0, 30)}... (${item.difficulty} logits)`}
                />
              ))}
            </div>
            <div className="flex justify-between text-xs text-gray-600">
              <span>Very Easy</span>
              <span>Moderate</span>
              <span>Very Difficult</span>
            </div>
          </div>

          {/* Detailed Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-4 py-3 font-medium text-gray-700">Question</th>
                  <th className="px-4 py-3 font-medium text-gray-700">Difficulty</th>
                  <th className="px-4 py-3 font-medium text-gray-700">Infit</th>
                  <th className="px-4 py-3 font-medium text-gray-700">Outfit</th>
                  <th className="px-4 py-3 font-medium text-gray-700">Discrimination</th>
                  <th className="px-4 py-3 font-medium text-gray-700">Responses</th>
                  <th className="px-4 py-3 font-medium text-gray-700">Fit Status</th>
                  <th className="px-4 py-3 font-medium text-gray-700">Recommendation</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {analysisResults.items.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-gray-800">
                      {item.text?.length > 60 ? `${item.text.substring(0, 60)}...` : item.text}
                    </td>
                    <td className="px-4 py-3">
                      {item.totalResponses > 0 ? (
                        <div className="flex items-center">
                          <span className="font-medium text-gray-800">{item.difficulty}</span>
                          {item.difficulty > 0.5 ? (
                            <SafeIcon icon={FiArrowUp} className="ml-1 text-red-500" />
                          ) : item.difficulty < -0.5 ? (
                            <SafeIcon icon={FiArrowDown} className="ml-1 text-green-500" />
                          ) : (
                            <SafeIcon icon={FiMinus} className="ml-1 text-gray-500" />
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">No data</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {item.totalResponses > 0 ? (
                        <span className={
                          item.infit >= 0.7 && item.infit <= 1.3
                            ? 'text-green-600 font-medium'
                            : item.infit < 0.5 || item.infit > 1.5
                              ? 'text-red-600 font-medium'
                              : 'text-yellow-600 font-medium'
                        }>
                          {item.infit}
                        </span>
                      ) : (
                        <span className="text-gray-400">No data</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {item.totalResponses > 0 ? (
                        <span className={
                          item.outfit >= 0.7 && item.outfit <= 1.3
                            ? 'text-green-600 font-medium'
                            : item.outfit < 0.5 || item.outfit > 1.5
                              ? 'text-red-600 font-medium'
                              : 'text-yellow-600 font-medium'
                        }>
                          {item.outfit}
                        </span>
                      ) : (
                        <span className="text-gray-400">No data</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {item.totalResponses > 0 ? (
                        <span className={
                          item.discrimination >= 0.7 
                            ? 'text-green-600 font-medium' 
                            : item.discrimination >= 0.3
                              ? 'text-yellow-600 font-medium'
                              : 'text-red-600 font-medium'
                        }>
                          {item.discrimination}
                        </span>
                      ) : (
                        <span className="text-gray-400">No data</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {item.totalResponses > 0 ? (
                        <span className="font-medium">
                          {item.correctResponses}/{item.totalResponses}
                        </span>
                      ) : (
                        <span className="text-gray-400">No data</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {item.totalResponses > 0 ? (
                        <div className="flex items-center">
                          <SafeIcon 
                            icon={getStatusIcon(item.fitStatus)} 
                            className={`mr-1 ${getStatusColor(item.fitStatus)}`} 
                          />
                          <span className={`font-medium ${getStatusColor(item.fitStatus)}`}>
                            {item.fitStatus.charAt(0).toUpperCase() + item.fitStatus.slice(1)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400">No data</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700">
                      {item.totalResponses > 0 ? item.recommendation : "No response data available"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Information Section */}
      <div className="mt-6 bg-indigo-50 p-4 rounded-lg border border-indigo-100">
        <h4 className="text-sm font-semibold text-indigo-800 mb-2 flex items-center">
          <SafeIcon icon={FiInfo} className="mr-2" />
          About Rasch Analysis
        </h4>
        <p className="text-xs text-indigo-700">
          Rasch analysis evaluates items based on their fit to the Rasch measurement model, which assumes that the probability of a correct response is a function of person ability and item difficulty. Good items have infit and outfit values between 0.7 and 1.3, and high discrimination parameters (&gt; 0.5).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
          <div className="bg-white p-3 rounded border border-indigo-100">
            <h5 className="text-xs font-medium text-indigo-800">Difficulty (logits)</h5>
            <p className="text-xs text-gray-600">
              Measures item challenge level on logit scale. Higher values = more difficult items.
            </p>
          </div>
          <div className="bg-white p-3 rounded border border-indigo-100">
            <h5 className="text-xs font-medium text-indigo-800">Infit/Outfit</h5>
            <p className="text-xs text-gray-600">
              Measures how well items fit the Rasch model. Ideal range: 0.7-1.3.
            </p>
          </div>
          <div className="bg-white p-3 rounded border border-indigo-100">
            <h5 className="text-xs font-medium text-indigo-800">Discrimination</h5>
            <p className="text-xs text-gray-600">
              How well items separate high and low ability students. Higher = better.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RaschAnalysis;